/**
 * Task 9 — the only place the Error Suppression screens talk to data.
 *
 * Every screen calls this module and nothing else. That was what made the Phase 2 switch from sample
 * data to the real API a change in one file rather than in each page, and it is why no component
 * anywhere constructs a suppression URL or header of its own.
 *
 * It owns the two pieces of per-request plumbing every page would otherwise repeat: building the
 * Authorisation / CloudCoverToken header pair, and mapping a 440 to the session timeout redirect.
 *
 * THE ORGANISATION IS SENT FROM HERE (A1, answered 2026-09-08).
 *
 * It did not used to be. The front-end proxy resolved it from the signed CloudCoverToken's OI
 * claim on the grounds that a caller-supplied organisation id is untrusted — which is true, and
 * which was implemented as "do not accept one at all". That removed the only channel that could
 * carry the MANAGED organisation, and since the token is not reissued when an administrator
 * switches organisation, every reseller and master administrator silently operated on their own
 * organisation instead of the customer's.
 *
 * Accepting a value and trusting it are different things. The managed organisation now travels as
 * the ManageOrgId header, and the resolver proves it against the backend before it may widen scope
 * — see CoreAPIFrontEnd TokenOrganizationScopeResolver. Nothing here is an authorisation input.
 */
import Axios from "axios";
import { API_ROUTE, Routes } from "./Constants";
import { currentManagedOrg } from "./SuppressionOrg";

/**
 * The severities a known error must have to be suppressible (B3).
 *
 * Only Error and Warning entries affect a job's status, so suppressing an Info entry could never have
 * a visible effect. The server applies this; the constant stays here because the severity filter on
 * the Error Suppression page must offer exactly these options and no others.
 */
export const SUPPRESSIBLE_SEVERITIES = ["Error", "Warning"];

/**
 * Builds the headers every authenticated call in this SPA sends.
 *
 * Every function below routes through here, so the managed organisation is attached to all ten
 * reads and writes in one place — and this stays the only place in the SPA that builds a
 * suppression header.
 */
async function buildConfig(setAuthContext) {
    // Store/Authentication-Context.js takes a single argument. Several older pages call it as
    // setAuthContext("", new Date()), which silently discards the timestamp and disables the
    // 20-minute refresh check — do not copy that here.
    const token = await setAuthContext(new Date());
    const headers = {
        Authorisation: `Bearer ${token}`,
        CloudCoverToken: sessionStorage.getItem("CloudCoverToken"),
    };

    // Omitted entirely when no organisation is in context, which is what keeps the API's
    // no-header path — the ordinary self-service case — behaving exactly as it did before A1.
    // For a plain organisation administrator this equals their login organisation, so the server
    // resolves it without an ownership round trip.
    const { orgId } = currentManagedOrg();
    if (orgId) headers.ManageOrgId = orgId;

    return { headers };
}

/**
 * Shared failure handling. A 440 means the CloudCoverToken expired; every page in this app
 * responds by sending the user to the timeout handler, so do it here once.
 *
 * ALWAYS RETURNS A STRING (or null for the redirect case). Callers hand the result straight to
 * toast.error, which renders it as a React child, so an object would surface as "[object Object]"
 * or throw during render.
 *
 * The two 403 sources on this feature disagree about shape, which is why this cannot just return
 * response.data:
 *   - SuppressionAccessAttribute.Deny  -> { "message": "Forbidden" }   (an object)
 *   - JobErrorSuppressionController    -> "Forbidden"                  (a string)
 * The first was rarely reachable before A1, because the SPA hides the actions a View-only user
 * cannot perform. It is now an ordinary outcome: any stale managed organisation produces it.
 */
export function handleApiError(err, fallbackMessage) {
    if (err?.response?.status === 440) {
        window.location.href = "/TimeoutErrorHandler";
        return null;
    }

    const data = err?.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    // The shape the authorization filter returns.
    if (data && typeof data.message === "string" && data.message.trim()) return data.message;

    return fallbackMessage;
}

/**
 * The known error catalog with this organization's suppression state folded in.
 *
 * Returns { items, accessLevel }. accessLevel is what the caller may do — the screens render actions
 * from it, so it is read from the response rather than inferred in the browser.
 */
export async function getKnownErrors(setAuthContext, filter = {}) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.post(API_ROUTE + Routes.GET_SUPPRESSIBLE_KNOWN_ERRORS, filter, config);
    return response.data;
}

/** One known error, plus its state on every job in the organization. */
export async function getKnownErrorDetail(setAuthContext, knownErrorId) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.get(API_ROUTE + Routes.GET_SUPPRESSIBLE_KNOWN_ERROR(knownErrorId), config);
    return response.data;
}

/** The organization's backup jobs, for the job picker. */
export async function getJobs(setAuthContext) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.get(API_ROUTE + Routes.GET_SUPPRESSION_JOBS, config);
    return response.data;
}

/** Every active suppression across the organization's jobs. */
export async function getActiveSuppressions(setAuthContext, filter = {}) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.post(API_ROUTE + Routes.GET_ACTIVE_SUPPRESSIONS, filter, config);
    return response.data;
}

/**
 * D4 — suppressions that ended within the visibility window, so a customer can re-enable one they
 * did not mean to lose. Expired and user-removed both appear; deactivation-cascade deletions do not.
 */
export async function getRecentlyEndedSuppressions(setAuthContext, filter = {}) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.post(API_ROUTE + Routes.GET_RECENTLY_ENDED_SUPPRESSIONS, filter, config);
    return response.data;
}

/**
 * Creates a suppression for each selected job in one action.
 *
 * payload: { knownErrorId, jobIds: [...], suppressedUntil, note }
 *
 * C1: ONE duration for the whole action, applied to every selected job. One row per job is created,
 * so each can be amended or removed individually afterwards. `note` is the optional 80-character
 * justification (C5), length-checked by SuppressionDuration.validateNote before the call is made.
 */
export async function createSuppressions(setAuthContext, payload) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.post(API_ROUTE + Routes.CREATE_SUPPRESSION, payload, config);
    return response.data;
}

/** Changes an existing suppression's expiry and justification. payload: { suppressedUntil, note } */
export async function amendSuppression(setAuthContext, suppressionId, payload) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.put(API_ROUTE + Routes.AMEND_SUPPRESSION(suppressionId), payload, config);
    return response.data;
}

/** Removes a single suppression. */
export async function removeSuppression(setAuthContext, suppressionId) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.delete(API_ROUTE + Routes.REMOVE_SUPPRESSION(suppressionId), config);
    return response.data;
}

/** Removes every suppression for one known error across all of the organization's jobs. */
export async function removeAllSuppressionsForError(setAuthContext, knownErrorId) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.delete(API_ROUTE + Routes.REMOVE_SUPPRESSIONS_FOR_ERROR(knownErrorId), config);
    return response.data;
}

/**
 * D4 — puts an ended suppression back, with a fresh duration.
 *
 * payload: { suppressedUntil, note }. The expiry must be chosen again rather than restored: the old
 * one is in the past by definition, so reinstating it would create a rule that is already expired.
 */
export async function reinstateSuppression(setAuthContext, suppressionId, payload) {
    const config = await buildConfig(setAuthContext);
    const response = await Axios.post(API_ROUTE + Routes.REINSTATE_SUPPRESSION(suppressionId), payload, config);
    return response.data;
}
